import { DateValue } from '@internationalized/date';
import { AssistantAggregateType } from '@rematter/frontend-lib-api-client';
import { atom, useAtom } from 'jotai';

import { assistantOutboxEventCountsQueryAtom } from '../_graphql/queryAssistantOutboxEventCounts';
import { assistantOutboxEventsQueryAtom } from '../_graphql/queryAssistantOutboxEvents';

import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

export type AssistantAuditTab = 'events' | 'inbox';

export const $assistantAuditTab = atom<AssistantAuditTab>('events');

export const $assistantAggregateTypeFilter = atom<
  AssistantAggregateType | undefined
>(undefined);

export const $assistantAggregateIdFilter = atom<string | undefined>(undefined);

export const $assistantPagination = atom<{ offset: number; limit: number }>({
  offset: 0,
  limit: 50,
});

export const $selectedAssistantEventId = atom<string | undefined>(undefined);

export const $assistantDateRangeFilter = atom<{
  start: DateValue;
  end: DateValue;
} | null>(null);

export const $remoteAssistantOutboxEvents = assistantOutboxEventsQueryAtom(
  (get) => {
    const aggregateType = get($assistantAggregateTypeFilter);
    const aggregateId = get($assistantAggregateIdFilter);
    const pagination = get($assistantPagination);
    const dateRange = get($assistantDateRangeFilter);

    return {
      variables: {
        aggregateTypes: aggregateType ? [aggregateType] : undefined,
        aggregateId,
        pagination,
        createdAtRange:
          dateRange?.start && dateRange?.end
            ? {
                start: dateToZonedDateTimeStart(dateRange.start),
                end: dateToZonedDateTimeEnd(dateRange.end),
              }
            : undefined,
      },
    };
  },
);

export const $remoteAssistantOutboxEventCounts =
  assistantOutboxEventCountsQueryAtom();

export const useAssistantAuditTab = () => {
  const [tab, setTab] = useAtom($assistantAuditTab);
  return [tab, setTab] as const;
};

export const useAssistantAggregateTypeFilter = () => {
  const [filter, setFilter] = useAtom($assistantAggregateTypeFilter);
  const [, setAggregateId] = useAtom($assistantAggregateIdFilter);

  const setFilterAndResetId = (value: AssistantAggregateType | undefined) => {
    setFilter(value);
    setAggregateId(undefined);
  };

  return [filter, setFilterAndResetId] as const;
};

export const useAssistantAggregateIdFilter = () => {
  const [filter, setFilter] = useAtom($assistantAggregateIdFilter);
  return [filter, setFilter] as const;
};

export const useAssistantPagination = () => {
  const [pagination, setPagination] = useAtom($assistantPagination);
  return [pagination, setPagination] as const;
};

export const useSelectedAssistantEventId = () => {
  const [selectedId, setSelectedId] = useAtom($selectedAssistantEventId);
  return [selectedId, setSelectedId] as const;
};

export const useAssistantDateRangeFilter = () => {
  const [filter, setFilter] = useAtom($assistantDateRangeFilter);
  return [filter, setFilter] as const;
};
